import React, { useState, useEffect, useRef } from "react";
import {
  Stage,
  Layer,
  Image as KonvaImage,
  Text,
  Transformer,
} from "react-konva";
import ShapeSelector from "./ShapeSelector";
import "./HomeComp.css";
import CustomInnerCanvas from "./CustomInnerCanvas";

const CustomCanvas = ({
  image,
  currentBkgShape,
  backgroundColor,
  canvasText,
  textColor,
  uploadLogo,
  currentFont,
  coverRef,
  innerPageOption,
  innerRef,
  selectedId,
  setSelectedId,
  innerPageText,
  innerTextColor,
  currentInnerFont,
  uploadInnerLogo,
}: any) => {
  const [imgProps, setImgProps] = useState({ width: 0, height: 0 });
  const [logoImage, setLogoImage] = useState<HTMLImageElement | null>(null);

  // Centered shape position after canvas loads
  const [shapeProps, setShapeProps] = useState({
    width: currentBkgShape === "circle" ? 200 : 220,
    height: currentBkgShape === "circle" ? 200 : 120,
    fill: backgroundColor,
    shadowBlur: 1,
    stroke: "white",
    id: "shape",
    x: 0,
    y: 100,
    draggable: true,
  });

  const [logoProps, setLogoProps] = useState({
    width: 0,
    height: 0,
    x: 0,
    y: 0,
    draggable: true,
  });

  const [textProps, setTextProps] = useState({
    text: Array.isArray(canvasText) ? canvasText.join("\n") : canvasText,
    fontFamily: currentFont,
    fontStyle: currentFont?.includes("italic") ? "italic" : "normal",
    x: 0,
    y: 100,
    draggable: true,
  });

  const textRef = useRef<any>(null);
  const trRef = useRef<any>(null);
  const logoRef = useRef<any>(null);

  const canvasWidth = imgProps.width;
  const canvasHeight = imgProps.height;

  const handleSelect = (e: any) => {
    setSelectedId(e.target.id());
  };

  const handleDeselect = (e: any) => {
    if (e.target === e.target.getStage()) {
      setSelectedId(null);
    }
  };

  useEffect(() => {
    if (image) {
      const img = new window.Image();
      img.src = image.src;
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        const maxW = window.innerWidth * 0.9; // 90% width for mobile
        const maxH = window.innerHeight * 0.7;

        const ratio = img.width / img.height;

        if (width > maxW) {
          width = maxW;
          height = maxW / ratio;
        }
        if (height > maxH) {
          height = maxH;
          width = maxH * ratio;
        }

        setImgProps({ width, height });

        // Center shape & text based on image size
        setShapeProps((prev) => ({
          ...prev,
          x: width / 2 - prev.width / 2,
        }));

        setTextProps((prev) => ({
          ...prev,
          x: width / 2 - 110, // since text width = 220
        }));
      };
    }
  }, [image]);

  // Logo upload logic
  useEffect(() => {
    if (uploadLogo && uploadLogo !== "") {
      const logo = new window.Image();
      logo.src = URL.createObjectURL(uploadLogo);

      logo.onload = () => {
        const ratio = logo.width / logo.height;

        const maxLogoWidth = canvasWidth * 0.5;
        const maxLogoHeight = canvasHeight * 0.5;

        let w = maxLogoWidth;
        let h = w / ratio;

        if (h > maxLogoHeight) {
          h = maxLogoHeight;
          w = h * ratio;
        }

        setLogoImage(logo);
        setLogoProps({
          width: w,
          height: h,
          x: (canvasWidth - w) / 2,
          y: (canvasHeight - h) / 2,
          draggable: true,
        });
      };

      return () => URL.revokeObjectURL(logo.src);
    }

    if (uploadLogo === "") setLogoImage(null);
  }, [uploadLogo, canvasWidth, canvasHeight]);

  // Text changes
  useEffect(() => {
    setTextProps((old) => ({
      ...old,
      text: Array.isArray(canvasText) ? canvasText.join("\n") : canvasText,
      fontFamily: currentFont,
      fontStyle: currentFont?.includes("italic") ? "italic" : "normal",
    }));
  }, [canvasText, currentFont]);

  return (
    <div>
      <div style={{ display: innerPageOption === "Cover" ? "block" : "none" }}>
        {/* --- CENTER CANVAS WRAPPER --- */}
        <div className="canvas-wrapper">
          <Stage
            width={imgProps.width}
            height={imgProps.height}
            onMouseDown={handleDeselect}
            onTouchStart={handleDeselect}
            ref={coverRef}
          >
            <Layer>
              {image && (
                <KonvaImage
                  image={image}
                  width={imgProps.width}
                  height={imgProps.height}
                />
              )}
            </Layer>

            <Layer>
              <ShapeSelector
                backgroundColor={backgroundColor}
                currentBkgShape={currentBkgShape}
                shapeProps={shapeProps}
                isSelected={selectedId === "shape"}
                onSelect={handleSelect}
                onTransform={() => {}}
                onDragEnd={(e: any) =>
                  setShapeProps({
                    ...shapeProps,
                    x: e.target.x(),
                    y: e.target.y(),
                  })
                }
              />

              <Text
                text={textProps.text}
                fontSize={20}
                fill={textColor}
                x={textProps.x}
                y={textProps.y}
                width={220}
                align="center"
                draggable
                onClick={handleSelect}
                onTap={handleSelect}
                onDragEnd={(e) =>
                  setTextProps({
                    ...textProps,
                    x: e.target.x(),
                    y: e.target.y(),
                  })
                }
                id="text"
                ref={textRef}
                fontFamily={textProps.fontFamily}
                fontStyle={textProps.fontStyle}
              />

              {selectedId === "text" && (
                <Transformer ref={trRef} nodes={[textRef.current]} keepRatio />
              )}
            </Layer>

            <Layer>
              {logoImage && (
                <KonvaImage
                  image={logoImage}
                  x={logoProps.x}
                  y={logoProps.y}
                  width={logoProps.width}
                  height={logoProps.height}
                  draggable
                  ref={logoRef}
                  onClick={handleSelect}
                  onTap={handleSelect}
                  onDragEnd={(e) =>
                    setLogoProps({
                      ...logoProps,
                      x: e.target.x(),
                      y: e.target.y(),
                    })
                  }
                  id="logo"
                />
              )}

              {selectedId === "logo" && logoRef.current && (
                <Transformer ref={trRef} nodes={[logoRef.current]} />
              )}
            </Layer>
          </Stage>
        </div>
      </div>

      <div style={{ display: innerPageOption === "Cover" ? "none" : "block" }}>
        <CustomInnerCanvas
          innerPageOption={innerPageOption}
          innerRef={innerRef}
          innerPageText={innerPageText}
          innerTextColor={innerTextColor}
          currentInnerFont={currentInnerFont}
          uploadInnerLogo={uploadInnerLogo}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
        />
      </div>
    </div>
  );
};

export default CustomCanvas;
